import React, { useState } from "react";
import { UploadKycApi } from "../../../apis/login/upload_kyc_api"; // Import your API helper
import { API_STATUS } from "../../../constants";

export default function VerifyMobile({
    genOtp,
    mobileError,
    setMobileError,
    setMobile,
    validateMobile,
    maskedMobile,
    setStep,
    onOtherWaySubmit,
    submitDataAlt
}) {
    const [UploadApiStatus, setUploadApiStatus] = useState(API_STATUS);
    const [isOtherWay, setIsOtherWay] = useState(false);

    // Form & Upload States
    const [altMobile, setAltMobile] = useState('');
    const [uploadToken, setUploadToken] = useState(null);
    const [formError, setFormError] = useState('');

    // Specific field-level error state
    const [errors, setErrors] = useState({
        mobile: '',
        gst: '',
        panFront: '',
        panBack: ''
    });

    // File Upload Status Tracking
    const [uploads, setUploads] = useState({
        gst: { file: null, status: 'idle', url: '' },      // 'idle' | 'uploading' | 'success' | 'error'
        panFront: { file: null, status: 'idle', url: '' },
        panBack: { file: null, status: 'idle', url: '' }
    });

    // Helper: Generate or reuse shared upload token
    const getOrCreateToken = () => {
        if (uploadToken) return uploadToken;
        const newToken = 'UT_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
        setUploadToken(newToken);
        return newToken;
    };

    // Generic handler for individual file selection & instant upload
    const handleFileUpload = async (e, fileType) => {
        const file = e.target.files[0];
        if (!file) return;

        // Reset field specific error on new selection
        setErrors(prev => ({ ...prev, [fileType]: '' }));
        setFormError('');

        // Type validations
        if (fileType === 'gst' && file.type !== 'application/pdf') {
            setErrors(prev => ({ ...prev, gst: 'GST Certificate must be a valid PDF file.' }));
            e.target.value = null;
            return;
        }
        if ((fileType === 'panFront' || fileType === 'panBack') && !file.type.startsWith('image/')) {
            const fieldName = fileType === 'panFront' ? 'PAN Front' : 'PAN Back';
            setErrors(prev => ({ ...prev, [fileType]: `${fieldName} must be an image file (JPG, PNG).` }));
            e.target.value = null;
            return;
        }

        const activeToken = getOrCreateToken();

        // Update state to loading
        setUploads(prev => ({
            ...prev,
            [fileType]: { file, status: 'uploading', url: '' }
        }));

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('docType', fileType);
            formData.append('upload_token', activeToken);

            const res = await UploadKycApi(formData, setUploadApiStatus);

            setUploads(prev => ({
                ...prev,
                [fileType]: { file, status: 'success', url: res?.filePath || '' }
            }));
        } catch (error) {
            setErrors(prev => ({ ...prev, [fileType]: `Failed to upload document. Please try again.` }));
            setUploads(prev => ({
                ...prev,
                [fileType]: { ...prev[fileType], status: 'error' }
            }));
        }
    };

    // Thorough Validation Function
    const validateForm = () => {
        let isValid = true;
        const newErrors = { mobile: '', gst: '', panFront: '', panBack: '' };

        // 1. Mobile Validation (Indian 10-digit number format check)
        const mobileRegex = /^[6-9]\d{9}$/;
        if (!altMobile) {
            newErrors.mobile = 'Mobile number is required.';
            isValid = false;
        } else if (!mobileRegex.test(altMobile)) {
            newErrors.mobile = 'Enter a valid 10-digit mobile number starting with 6-9.';
            isValid = false;
        }

        // 2. GST File Validation
        if (!uploads.gst.file) {
            newErrors.gst = 'GST Certificate is required.';
            isValid = false;
        } else if (uploads.gst.status === 'uploading') {
            newErrors.gst = 'GST Certificate is still uploading...';
            isValid = false;
        } else if (uploads.gst.status !== 'success') {
            newErrors.gst = 'GST Certificate upload failed. Please re-upload.';
            isValid = false;
        }

        // 3. PAN Front Validation
        if (!uploads.panFront.file) {
            newErrors.panFront = 'PAN Front image is required.';
            isValid = false;
        } else if (uploads.panFront.status === 'uploading') {
            newErrors.panFront = 'PAN Front is still uploading...';
            isValid = false;
        } else if (uploads.panFront.status !== 'success') {
            newErrors.panFront = 'PAN Front upload failed. Please re-upload.';
            isValid = false;
        }

        // 4. PAN Back Validation
        if (!uploads.panBack.file) {
            newErrors.panBack = 'PAN Back image is required.';
            isValid = false;
        } else if (uploads.panBack.status === 'uploading') {
            newErrors.panBack = 'PAN Back is still uploading...';
            isValid = false;
        } else if (uploads.panBack.status !== 'success') {
            newErrors.panBack = 'PAN Back upload failed. Please re-upload.';
            isValid = false;
        }

        setErrors(newErrors);

        if (!isValid) {
            setFormError('Please resolve all validation errors below before submitting.');
        } else {
            setFormError('');
        }

        return isValid;
    };

    const handleAltSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        const payload = {
            mobile: altMobile,
            upload_token: uploadToken,
            gst_url: uploads.gst.url,
            pan_front_url: uploads.panFront.url,
            pan_back_url: uploads.panBack.url
        };

        if (submitDataAlt) {
            submitDataAlt({ mobile: altMobile, uploadToken: uploadToken });
        } else {
            console.log("Updating User with Upload Token:", payload);
        }
    };

    const renderUploadBadge = (status) => {
        if (status === 'uploading') return <span className="spinner-border spinner-border-sm text-primary ms-2" />;
        if (status === 'success') return <span className="badge bg-success ms-2">Uploaded</span>;
        if (status === 'error') return <span className="badge bg-danger ms-2">Failed</span>;
        return null;
    };

    return (
        <div>
            {isOtherWay ? (
                /* ALTERNATIVE FLOW */
                <div className="text-start px-4">
                    <h5 className="text-center mb-3 fw-bold">Document Verification</h5>

                    {formError && (
                        <div className="alert alert-danger py-2 text-center small" role="alert">
                            {formError}
                        </div>
                    )}

                    <form onSubmit={handleAltSubmit} noValidate>
                        {/* Mobile Field */}
                        <div className="mb-3">
                            <label className="form-label fw-bold small mb-1">
                                Mobile Number <span className="text-danger">*</span>
                            </label>
                            <input
                                type="tel"
                                className={`form-control ${errors.mobile ? 'is-invalid' : ''}`}
                                placeholder="Enter 10-digit mobile number"
                                maxLength={10}
                                value={altMobile}
                                onChange={(e) => {
                                    setAltMobile(e.target.value.replace(/\D/g, ''));
                                    setErrors(prev => ({ ...prev, mobile: '' }));
                                }}
                            />
                            {errors.mobile && <div className="invalid-feedback">{errors.mobile}</div>}
                        </div>

                        {/* GST Certificate Field */}
                        <div className="mb-3">
                            <label className="form-label fw-bold small mb-1">
                                Upload GST Certificate (PDF) <span className="text-danger">*</span> {renderUploadBadge(uploads.gst.status)}
                            </label>
                            <input
                                type="file"
                                className={`form-control ${errors.gst ? 'is-invalid' : ''}`}
                                accept="application/pdf"
                                onChange={(e) => handleFileUpload(e, 'gst')}
                            />
                            {errors.gst && <div className="invalid-feedback d-block">{errors.gst}</div>}
                        </div>

                        {/* PAN Front & Back Fields */}
                        <div className="row">
                            <div className="col-6 mb-3">
                                <label className="form-label fw-bold small mb-1">
                                    PAN Front <span className="text-danger">*</span> {renderUploadBadge(uploads.panFront.status)}
                                </label>
                                <input
                                    type="file"
                                    className={`form-control ${errors.panFront ? 'is-invalid' : ''}`}
                                    accept="image/*"
                                    onChange={(e) => handleFileUpload(e, 'panFront')}
                                />
                                {errors.panFront && <div className="invalid-feedback d-block">{errors.panFront}</div>}
                            </div>
                            <div className="col-6 mb-3">
                                <label className="form-label fw-bold small mb-1">
                                    PAN Back <span className="text-danger">*</span> {renderUploadBadge(uploads.panBack.status)}
                                </label>
                                <input
                                    type="file"
                                    className={`form-control ${errors.panBack ? 'is-invalid' : ''}`}
                                    accept="image/*"
                                    onChange={(e) => handleFileUpload(e, 'panBack')}
                                />
                                {errors.panBack && <div className="invalid-feedback d-block">{errors.panBack}</div>}
                            </div>
                        </div>

                        <div className="d-grid gap-2 mt-3">
                            <button
                                type="submit"
                                className="button_slide slide_right orange w-100 border-0 py-2"
                                disabled={
                                    uploads.gst.status === 'uploading' ||
                                    uploads.panFront.status === 'uploading' ||
                                    uploads.panBack.status === 'uploading'
                                }
                            >
                                Submit Verification
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-link text-decoration-none text-secondary"
                                onClick={() => {
                                    setIsOtherWay(false);
                                    setFormError('');
                                    setErrors({ mobile: '', gst: '', panFront: '', panBack: '' });
                                }}
                            >
                                Back to Mobile Verification
                            </button>
                        </div>
                    </form>
                </div>
            ) : (
                /* ORIGINAL FLOW */
                <>
                    <div className="col-lg-12 col-md-12 col-sm-12 col-12 mb-3" style={{ textAlign: 'center' }}>
                        <div style={{ textAlign: 'center', paddingLeft: 3 }}>Enter complete mobile number</div>
                        <div className="mb-3">
                            <div className="contact-field" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                                <input className="form-control" type="text" value={maskedMobile.slice(0, 2)} style={{ width: 50 }} readOnly />
                                <input
                                    onChange={(e) => {
                                        setMobileError('');
                                        setMobile(e.target.value);
                                    }}
                                    className="form-control"
                                    type="text"
                                    name="mobile"
                                    placeholder="******"
                                    style={{ textAlign: 'center', width: 200 }}
                                />
                                <input className="form-control" type="text" value={maskedMobile.slice(-2)} style={{ width: 50 }} readOnly />
                            </div>
                        </div>
                        {mobileError !== "" && (
                            <div className='text-danger' style={{ textAlign: 'center' }}>{mobileError}</div>
                        )}
                    </div>

                    <div className="text-center mb-2">
                        <button
                            type="button"
                            className="btn btn-link text-decoration-none p-0"
                            style={{ fontSize: '14px', color: '#0d6efd' }}
                            onClick={() => {
                                setIsOtherWay(true);
                                setMobileError('');
                            }}
                        >
                            Try other way
                        </button>
                    </div>

                    <div className="row mt-3">
                        <div className='col-6' style={{ textAlign: 'left', paddingLeft: 50 }}>
                            <button
                                onClick={() => setStep(1)}
                                className='btn btn-outline-danger'>Back</button>
                        </div>
                        <div className="col-6" style={{ textAlign: 'right', paddingRight: 50 }}>
                            {genOtp.isLoader ? (
                                <div className="spinner-border text-primary" role="status"></div>
                            ) : (
                                <button
                                    onClick={() => validateMobile()}
                                    type="button"
                                    className="button_slide slide_right orange"
                                >
                                    Verify
                                </button>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}